
































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { formatDigitsGrouping } from "../../../common/utils/formattingUtils"
import i18nService from "@/services/i18n";

type Column = 'action' | 'kat' | 'cls' | 'pcl' | 'spf' | 'region' | 'name'
    | 'plan_year_0' | 'fact_0101_1' | 'plan_date_0' | 'fact_date_0'
    | 'plan_year_1' | 'fact_0101_2' | 'plan_date_1' | 'fact_date_1' | 'otklonenie_1'
    | 'plan_year_2' | 'plan_date_2' | 'fact_date_2' | 'otklonenie_2'

export interface TableField {
    key: Column,
    label: string,
    active: boolean,
    valueIndex?: number  // 0-based index into row.values / VALUE_KEYS; undefined for non-value columns
}

// Порядок и подписи должны совпадать с VALUE_KEYS в backend report_svod_doh.py
const VALUE_KEYS: Column[] = [
    'plan_year_0', 'fact_0101_1', 'plan_date_0', 'fact_date_0',
    'plan_year_1', 'fact_0101_2', 'plan_date_1', 'fact_date_1', 'otklonenie_1',
    'plan_year_2', 'plan_date_2', 'fact_date_2', 'otklonenie_2',
]

// Number of rows kept in the DOM at any time
const WINDOW_SIZE = 200
// Rows rendered above/below the visible viewport as a scroll buffer
const SCROLL_BUFFER = 60

@Component({})
export default class ReportSvodDoh extends Vue {

    @Prop({
        type: Object,
        required: true,
    })
    public tableData!: any

    @Prop({
        type: String,
        required: true,
    })
    public roundUp!: string

    @Prop({
        type: Boolean,
        default: false,
    })
    public fillKbk!: boolean

    @Prop({
        type: Boolean,
        default: true,
    })
    public treeView!: boolean

    @Prop({
        type: Date,
        default: null,
    })
    public reportDate!: Date | null

    // Снимок пропсов на момент построения отчёта (клика "Сформировать"), а не live-значения
    // формы фильтров - иначе переключение тумблеров после построения меняло бы уже готовую
    // таблицу "на лету" некорректно, без пересчёта данных.
    private effectiveTreeView = true
    private effectiveFillKbk = false
    private effectiveRoundUp = ''

    private created() {
        this.effectiveTreeView = this.treeView
        this.effectiveFillKbk = this.fillKbk
        this.effectiveRoundUp = this.roundUp
        this.getRows();
        this.calcItems();
    }

    private mounted() {
        this.$nextTick(() => {
            const container = this.$refs.tableContainer as HTMLElement
            if (!container) return
            const firstRow = container.querySelector('tbody tr') as HTMLElement | null
            if (firstRow && firstRow.offsetHeight > 0) {
                this.rowHeight = firstRow.offsetHeight
            }
            this.initRowHeights(this.filteredRows.length)
            this.virtualEnd = Math.min(WINDOW_SIZE, this.filteredRows.length)
            window.addEventListener('scroll', this.onScroll, { passive: true, capture: true })
        })
    }

    private initRowHeights(count: number) {
        const rh = this.rowHeight
        this._rowHeights = new Array(count).fill(rh)
        this.rebuildPrefixSum()
    }

    private rebuildPrefixSum() {
        const n = this._rowHeights.length
        const ps = new Array(n + 1)
        ps[0] = 0
        for (let i = 0; i < n; i++) {
            ps[i + 1] = ps[i] + this._rowHeights[i]
        }
        this._prefixSum = ps
    }

    private findRowAtScrollPos(scrollPos: number): number {
        if (this._prefixSum.length < 2) return Math.floor(scrollPos / this.rowHeight)
        let lo = 0
        let hi = this._prefixSum.length - 2
        while (lo < hi) {
            const mid = (lo + hi + 1) >> 1
            if (this._prefixSum[mid] <= scrollPos) lo = mid
            else hi = mid - 1
        }
        return lo
    }

    private measureRenderedRows() {
        const container = this.$refs.tableContainer as HTMLElement
        if (!container || !this._rowHeights.length) return
        const rows = container.querySelectorAll<HTMLElement>('tbody tr[data-row-index]')
        let changed = false
        rows.forEach(row => {
            const idx = parseInt(row.dataset.rowIndex ?? '')
            const h = row.offsetHeight
            if (!isNaN(idx) && h > 0 && idx < this._rowHeights.length && this._rowHeights[idx] !== h) {
                this._rowHeights[idx] = h
                changed = true
            }
        })
        if (changed) this.rebuildPrefixSum()
    }

    private beforeDestroy() {
        window.removeEventListener('scroll', this.onScroll, true)
        if (this._scrollRaf !== null) cancelAnimationFrame(this._scrollRaf)
        if (this._scrollPointerTimer !== null) clearTimeout(this._scrollPointerTimer)
    }

    public tr(key: any): string {
        if (typeof key !== 'string') {
            return ''
        }
        const translationKey = 'modules.budget_execution_monitoring.reports_constructor'
        return String(this.$t(`${translationKey}.${key}`))
    }

    private get nameSuffix(): string {
        return `${i18nService.locale}`
    }

    // При включённом "Заполнять КБК" отдельной колонки "Регион" нет (см. tableFields) - код
    // региона пишется в первой колонке-классификаторе КБК, как в "Отчете о кассовом исполнении".
    private get firstKbkLevelKey(): string | undefined {
        const levels: string[] = this.tableData.selected_levels || []
        return levels.find(l => l !== 'region')
    }

    private getCellText(row: any, key: string): string {
        let val = row[key]
        // "Заполнять КБК" дублирует только классификаторы КАТ/КЛ/ПКЛ/СПФ, не регион (как в
        // "Отчете о кассовом исполнении") - регион дублируется только в плоском режиме
        // (Древовидная структура выключена), где без него строка вообще не идентифицируема.
        const shouldFill = !this.effectiveTreeView || (this.effectiveFillKbk && key !== 'region')
        if ((val === undefined || val === null) && shouldFill) {
            val = row[`parent_${key}`]
        }
        if (this.effectiveFillKbk && key === this.firstKbkLevelKey && (val === undefined || val === null)) {
            val = row.region ?? row.parent_region
        }
        val = val ?? ''
        // КЛ и СПФ - двузначные коды по структуре ЕБК (dspk: 1 кат + 2 кл + 1 пкл + 2 спф),
        // выводим с ведущим нулём ("03", "01"), КАТ и ПКЛ - однозначные, без дополнения.
        if ((key === 'cls' || key === 'spf') && val !== '') {
            val = String(val).padStart(2, '0')
        }
        return `${val}`
    }

    // Наименование строки (отдельная колонка "Наименование") - имя того уровня, который эта
    // строка представляет напрямую (регион для строки-региона, категория для строки КАТ и т.д.).
    private getRowName(row: any): string {
        for (const level of this.tableData.selected_levels ?? []) {
            if (row[level] !== undefined && row[level] !== null) {
                return row[`${level}_name_${this.nameSuffix}`] ?? ''
            }
        }
        return ''
    }

    // Пустая строка (поле "Округлять результат до" не заполнено) трактуется как округление
    // до 0 знаков после запятой - целое число.
    private get roundMultiplier(): number {
        return Math.pow(10, Number(this.effectiveRoundUp || '0'))
    }

    private roundValue(v: number, multiplier: number): number {
        return Math.round(v * multiplier) / multiplier
    }

    private flattenData(data: any[], isFirstLevel: boolean = true, multiplier: number = this.roundMultiplier): any[] {
        const result: any[] = [];
        const childLists = ['kat_list', 'cls_list', 'pcl_list', 'spf_list', 'region_list'];

        for (const item of data) {
            const hasChildren = childLists.some(listKey => Array.isArray(item[listKey]) && item[listKey].length > 0);
            const isLeaf = !hasChildren;

            const flattenedItem: any = {
                ...item,
                values: item.values ?? null,
                visible: isFirstLevel,
                open: false,
                isLeaf: isLeaf
            };
            for (const key of childLists) delete flattenedItem[key];

            if (flattenedItem.values) {
                flattenedItem._valuesFormatted = Object.freeze(
                    (flattenedItem.values as number[]).map(
                        (v: number) => formatDigitsGrouping(this.roundValue(v, multiplier))
                    )
                )
                Object.freeze(flattenedItem.values)
            } else {
                flattenedItem._valuesFormatted = []
            }

            result.push(flattenedItem);

            for (const listKey of childLists) {
                if (Array.isArray(item[listKey])) {
                    result.push(...this.flattenData(item[listKey], false, multiplier));
                }
            }
        }

        return result;
    }

    private flattenDataFlat(data: any[], multiplier: number): any[] {
        const result: any[] = [];
        const childLists = ['kat_list', 'cls_list', 'pcl_list', 'spf_list', 'region_list'];

        for (const item of data) {
            const childKey = childLists.find(listKey => Array.isArray(item[listKey]) && item[listKey].length > 0);
            if (childKey) {
                result.push(...this.flattenDataFlat(item[childKey], multiplier));
                continue;
            }

            const flattenedItem: any = {
                ...item,
                values: item.values ?? null,
                visible: true,
                open: false,
                isLeaf: true,
            };
            for (const key of childLists) delete flattenedItem[key];

            if (flattenedItem.values) {
                flattenedItem._valuesFormatted = Object.freeze(
                    (flattenedItem.values as number[]).map(
                        (v: number) => formatDigitsGrouping(this.roundValue(v, multiplier))
                    )
                )
                Object.freeze(flattenedItem.values)
            } else {
                flattenedItem._valuesFormatted = []
            }

            result.push(flattenedItem);
        }

        return result;
    }

    private determineHierarchy(data: any[]): string[] {
        const hierarchy: string[] = [];
        let currentLevelItems = data;

        while (currentLevelItems && currentLevelItems.length > 0) {
            const firstItem = currentLevelItems[0];
            const currentLevelKey = Object.keys(firstItem).find(k =>
                typeof firstItem[k] === 'number' || typeof firstItem[k] === 'string'
            );
            if (!currentLevelKey || hierarchy.includes(currentLevelKey)) break;
            hierarchy.push(currentLevelKey);

            const nextListKey = Object.keys(firstItem).find(k => k.endsWith('_list') && Array.isArray(firstItem[k]));
            if (!nextListKey) break;
            currentLevelItems = firstItem[nextListKey];
        }

        return hierarchy;
    }

    private hierarchyLevels: string[] = [];

    private totalRow = {
        values: new Array(VALUE_KEYS.length).fill(0)
    }

    private getRows(): void {
        this.totalRow = { values: new Array(VALUE_KEYS.length).fill(0) }
        this.currentItems = [];

        const topLevelKey = Object.keys(this.tableData).find(k => k.endsWith('_list') && Array.isArray(this.tableData[k]));
        const topData = topLevelKey ? this.tableData[topLevelKey] : [];

        this.hierarchyLevels = this.determineHierarchy(topData);
        const multiplier = this.roundMultiplier
        this.currentItems = this.effectiveTreeView
            ? this.flattenData(topData, true, multiplier)
            : this.flattenDataFlat(topData, multiplier);

        for (const item of topData) {
            if (!item.values) continue
            for (let i = 0; i < item.values.length; i++) {
                this.totalRow.values[i] += item.values[i]
            }
        }
    }

    public currentItems: any[] = []

    private open = false;

    // Virtual scroll state
    private virtualStart = 0
    private virtualEnd = WINDOW_SIZE
    private rowHeight = 40
    private topSpacerHeight = 0
    private bottomSpacerHeight = 0
    private _scrollRaf: number | null = null
    private _scrollPointerTimer: any = null
    private isScrolling = false
    private _rowHeights: number[] = []
    private _prefixSum: number[] = [0]

    private get filteredRows(): any[] {
        return this.calcRows.filter(row => row.visible)
    }

    private get virtualRows(): any[] {
        return this.filteredRows.slice(this.virtualStart, this.virtualEnd)
    }

    private get activeColumnCount(): number {
        return this.activeTableFields.length
    }

    private syncVirtualWindow() {
        const container = this.$refs.tableContainer as HTMLElement
        if (!container) return

        let scrolledIntoContainer: number
        if (container.scrollTop > 0) {
            scrolledIntoContainer = container.scrollTop
        } else {
            const containerTop = container.getBoundingClientRect().top
            scrolledIntoContainer = Math.max(0, -containerTop)
        }

        const firstVisibleRow = this.findRowAtScrollPos(scrolledIntoContainer)
        const total = this.filteredRows.length

        const rawStart = Math.max(0, firstVisibleRow - SCROLL_BUFFER)
        const rawEnd = Math.min(total, rawStart + WINDOW_SIZE)
        const adjustedStart = Math.max(0, rawEnd - WINDOW_SIZE)

        this.virtualStart = adjustedStart
        this.virtualEnd = rawEnd

        const ps = this._prefixSum
        const end = Math.min(rawEnd, total)
        this.topSpacerHeight = ps.length > adjustedStart ? ps[adjustedStart] : adjustedStart * this.rowHeight
        this.bottomSpacerHeight = ps.length > total
            ? Math.max(0, ps[total] - ps[end])
            : Math.max(0, (total - end) * this.rowHeight)

        this.$nextTick(() => this.measureRenderedRows())
    }

    private onScroll() {
        if (!this.isScrolling) this.isScrolling = true
        if (this._scrollPointerTimer !== null) clearTimeout(this._scrollPointerTimer)
        this._scrollPointerTimer = setTimeout(() => { this.isScrolling = false }, 150)

        // eslint-disable-next-line eqeqeq
        if (this._scrollRaf != null) return
        this._scrollRaf = requestAnimationFrame(() => {
            try {
                this.syncVirtualWindow()
            } finally {
                this._scrollRaf = null
            }
        })
    }

    @Watch('filteredRows')
    private onFilteredRowsChanged() {
        this.$nextTick(() => {
            const container = this.$refs.tableContainer as HTMLElement
            if (container) {
                const firstRow = container.querySelector('tbody tr') as HTMLElement | null
                if (firstRow && firstRow.offsetHeight > 0) {
                    this.rowHeight = firstRow.offsetHeight
                }
            }
            this.initRowHeights(this.filteredRows.length)
            this.syncVirtualWindow()
        })
    }

    private thStyle(header: TableField): Record<string, string> {
        if (header.key === 'action') return { width: '50px' }
        if (header.key === 'name') return { width: '260px' }
        if (header.key === 'region') return { width: '90px' }
        if (this.availableLevels.includes(header.key)) return { width: '60px' }
        return { width: '130px' }
    }

    private availableLevels = ['region', 'kat', 'cls', 'pcl', 'spf'];

    private get years(): number[] {
        return this.tableData.years ?? []
    }

    // День/месяц из (даты фильтра - 1 день) + произвольный год, напр. при дате фильтра 28.03.2026
    // fmtDate(2024) -> "27.03.2024" - заголовки колонок должны показывать ту же дату, что реально
    // используется в расчёте (repdate = день-1), а не буквальную дату фильтра. Логика 1-в-1
    // совпадает с backend _repdate_for_year (включая fallback для 29 февраля).
    private fmtDate(y: number | undefined): string {
        if (y === undefined) return ''
        if (!this.reportDate) return `${y}`
        const ref = new Date(this.reportDate.getTime() - 24 * 60 * 60 * 1000)
        const day = ref.getDate()
        const month = ref.getMonth() + 1
        let d = new Date(y, month - 1, day)
        if (d.getMonth() + 1 !== month) {
            // day не существует в этом месяце/году (29 февраля невисокосного) - откат на 28-е
            d = new Date(y, month - 1, day - 1)
        }
        return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${y}`
    }

    private valueLabel(key: Column): string {
        const [y0, y1, y2] = this.years
        const labels: Record<string, string> = {
            plan_year_0: `План на год`,
            fact_0101_1: `Факт на 01.01.${y1 ?? ''}`,
            plan_date_0: `План на ${this.fmtDate(y0)}`,
            fact_date_0: `Факт на ${this.fmtDate(y0)}`,
            plan_year_1: `План на год`,
            fact_0101_2: `Факт на 01.01.${y2 ?? ''}`,
            plan_date_1: `План на ${this.fmtDate(y1)}`,
            fact_date_1: `Факт на ${this.fmtDate(y1)}`,
            otklonenie_1: `Отклонение факта ${this.fmtDate(y1)} от факта ${this.fmtDate(y0)}`,
            plan_year_2: `План на год`,
            plan_date_2: `План на ${this.fmtDate(y2)}`,
            fact_date_2: `Факт на ${this.fmtDate(y2)}`,
            otklonenie_2: `Отклонение факта ${this.fmtDate(y2)} от факта ${this.fmtDate(y1)}`,
        }
        return labels[key] ?? key
    }

    // Группировка колонок значений по годовым блокам для 2-уровневого заголовка таблицы,
    // как в шаблоне ТЗ (RQ.eAk.SvodDoh.13/14/15): 1-я строка - "{год} год" / "Отклонение...",
    // 2-я строка - подписи отдельных колонок (без года, он уже указан в 1-й строке).
    private get valueHeaderGroups(): { key: string, label: string, colspan: number, rowspan: number }[] {
        const [y0, y1, y2] = this.years
        return [
            { key: 'group_0', label: `${y0 ?? ''} год`, colspan: 4, rowspan: 1 },
            { key: 'group_1', label: `${y1 ?? ''} год`, colspan: 4, rowspan: 1 },
            { key: 'otklonenie_1', label: this.valueLabel('otklonenie_1'), colspan: 1, rowspan: 2 },
            { key: 'group_2', label: `${y2 ?? ''} год`, colspan: 3, rowspan: 1 },
            { key: 'otklonenie_2', label: this.valueLabel('otklonenie_2'), colspan: 1, rowspan: 2 },
        ]
    }

    private get fixedHeaderFields(): TableField[] {
        return this.activeTableFields.filter(f => f.valueIndex === undefined)
    }

    private get valueSubHeaderFields(): TableField[] {
        return this.activeTableFields.filter(f => f.valueIndex !== undefined && f.key !== 'otklonenie_1' && f.key !== 'otklonenie_2')
    }

    public allColumnsNameMap: Record<string, string> = {
        'action': '',
        'region': 'Регион',
        'kat': 'КАТ',
        'cls': 'КЛ',
        'pcl': 'ПКЛ',
        'spf': 'СПФ',
        'name': 'Наименование',
    }

    public get tableFields(): TableField[] {
        const selectedCols: string[] = this.tableData.selected_levels || [];

        const orderedKeys: string[] = ['action', ...selectedCols, 'name', ...VALUE_KEYS];
        return orderedKeys.map((key) => {
            const isAvailableLevel = this.availableLevels.includes(key) || key === 'name';
            const label = isAvailableLevel ? (this.allColumnsNameMap[key] ?? key) : this.valueLabel(key as Column);
            const valueIndex = VALUE_KEYS.indexOf(key as Column);

            // При включённом "Заполнять КБК" отдельной колонки "Регион" нет - код региона
            // пишется в первой колонке-классификаторе КБК (см. getCellText/firstKbkLevelKey),
            // как в "Отчете о кассовом исполнении".
            const active = !(key === 'region' && this.effectiveFillKbk)

            return {
                key: key as Column,
                label,
                active,
                ...(valueIndex !== -1 ? { valueIndex } : {}),
            };
        });
    }

    public get activeTableFields(): TableField[] {
        return this.tableFields.filter(f => f.active)
    }

    public formatDigitsGrouping = formatDigitsGrouping

    private openChilds(item: any) {
        item.open = !item.open
        const selectedLevels: string[] = this.tableData.selected_levels;
        const key = selectedLevels.find((level: any) => item[level] !== undefined);
        if (!key) return
        const keyIndx = selectedLevels.indexOf(key);

        if (item.open) {
            const nextKey = selectedLevels[keyIndx + 1]
            let childList = this.calcRows;
            for (let i = 0; i <= keyIndx; i++) {
                const childKey = selectedLevels[i]
                if (i === keyIndx) {
                    childList = childList.filter(curItem => curItem[nextKey] !== undefined && curItem['parent_' + childKey] === item[childKey])
                } else {
                    childList = childList.filter(curItem => curItem[nextKey] !== undefined && curItem['parent_' + childKey] === item['parent_' + childKey])
                }
            }
            for (const child of childList) {
                child.visible = item.open;
            }
        } else {
            for (let i = keyIndx; i < selectedLevels.length; i++) {
                const childKey = selectedLevels[i];
                const childLevel = this.calcRows.filter(curItem => curItem['parent_' + childKey] !== undefined && curItem['parent_' + childKey] === item[childKey])
                for (const child of childLevel) {
                    child.visible = item.open;
                    child.open = item.open;
                }
            }
        }
    }

    private openAll() {
        this.open = !this.open;
        const firstItem = this.tableData.selected_levels[0];
        for (const r of this.calcRows) {
            if (!r[firstItem]) {
                r.open = this.open;
                r.visible = this.open;
            } else {
                r.open = this.open;
            }
        }
    }

    private calcRows: any[] = []

    private calcItems() {
        this.calcRows = this.currentItems
    }
}
